/**
 * Client-side handling for `ACTIVE_SESSION_EXISTS` (APP_SPEC §21.1) — the
 * server's refusal to let one user hold two live sessions of the same game
 * in the same slot.
 *
 * Every way into a session can hit this (starting solo, hosting a lobby,
 * inviting an opponent, joining by code, accepting an invitation), and all
 * of them want the same recovery: name the game already in progress, offer
 * to resume it, or abandon it and retry what the user actually asked for.
 * That belongs in one place rather than five, so each caller only has to
 * route its mutation through `run` and render the shared dialog.
 *
 * Nothing here is game-specific (CLAUDE.md §2) — the blocking game's name
 * arrives in the server's conflict details.
 */
import { useCallback, useState } from 'react';

import { ApiError } from '@perplexicon/api-client';
import type { ActiveSessionConflict } from '@perplexicon/api-client';

import { useAbandonSession } from './games.js';

interface PendingConflict {
  conflict: ActiveSessionConflict;
  /** The action that was refused, replayed verbatim once the slot is free. */
  retry: () => void;
}

function conflictFrom(error: unknown): ActiveSessionConflict | null {
  if (!(error instanceof ApiError) || error.code !== 'ACTIVE_SESSION_EXISTS') return null;
  const details = error.details;
  if (typeof details !== 'object' || details === null) return null;
  const conflict = (details as { conflict?: unknown }).conflict;
  if (typeof conflict !== 'object' || conflict === null) return null;
  const candidate = conflict as Partial<ActiveSessionConflict>;
  // The dialog needs an id to resume/abandon and a name to say which game;
  // without both there is nothing useful to show, so fall back to the
  // caller's ordinary error banner instead of an empty dialog.
  if (typeof candidate.sessionId !== 'string' || typeof candidate.gameName !== 'string') {
    return null;
  }
  return candidate as ActiveSessionConflict;
}

/**
 * True when `error` is the one-active-session refusal. Source dialogs use
 * this to suppress their own error banner: the shared conflict dialog is
 * already explaining the same failure on top of them.
 */
export function isActiveGameConflict(error: unknown): boolean {
  return conflictFrom(error) !== null;
}

export interface ActiveGameConflictState {
  /** The blocking session, or `null` when there is nothing to resolve. */
  conflict: ActiveSessionConflict | null;
  /**
   * Call from a mutation's `onError` to route a conflict into the dialog.
   * Returns `true` when the error was a conflict and has been handled, so
   * the caller can leave its ordinary error banner to everything else.
   *
   * `retry` must be replayable — it is invoked again verbatim once the
   * blocking session has been abandoned.
   */
  capture: (error: unknown, retry: () => void) => boolean;
  /** Abandons the blocking session, then replays the refused action. */
  abandonAndRetry: () => void;
  dismiss: () => void;
  isAbandoning: boolean;
  abandonError: unknown;
}

export function useActiveGameConflict(): ActiveGameConflictState {
  const [pending, setPending] = useState<PendingConflict | null>(null);
  const abandon = useAbandonSession();

  const capture = useCallback((error: unknown, retry: () => void): boolean => {
    const conflict = conflictFrom(error);
    if (!conflict) return false;
    setPending({ conflict, retry });
    return true;
  }, []);

  const dismiss = useCallback(() => {
    setPending(null);
    abandon.reset();
  }, [abandon]);

  const abandonAndRetry = useCallback(() => {
    if (!pending) return;
    const { retry } = pending;
    abandon.mutate(pending.conflict.sessionId, {
      onSuccess: () => {
        setPending(null);
        retry();
      },
    });
  }, [abandon, pending]);

  return {
    conflict: pending?.conflict ?? null,
    capture,
    abandonAndRetry,
    dismiss,
    isAbandoning: abandon.isPending,
    abandonError: abandon.error,
  };
}
